import { useCallback } from 'react';
import { useLocalStorage } from '../../../Utilidades/useLocalStorage';

/**
 * Hook para manejar búsquedas recientes del localStorage.
 * 
 * @param {string|null} usuarioId - ID del usuario actual
 * @returns {Object} Estado y funciones para manejar recientes
 * 
 * @example
 * const { recientes, guardarReciente, eliminarReciente, limpiarRecientes } = useRecientes(usuarioId);
 */
export const useRecientes = (usuarioId) => {
  const STORAGE_KEY = 'recentSearches_ClassLimitless';

  const [recientes, setRecientes] = useLocalStorage(STORAGE_KEY, [], {
    userId: usuarioId,
    mode: 'array',
    dedupeBy: 'path',
    maxItems: 10,
    addTimestamp: true,
  });

  /**
   * Carga las búsquedas recientes desde localStorage.
   * (No-op: useLocalStorage ya maneja la carga inicial)
   * @param {boolean} forceRefresh - No utilizado (compatibilidad)
   */
  const cargarRecientes = useCallback(() => {
    // useLocalStorage maneja la carga inicial automáticamente
    // Este método existe por compatibilidad con la API anterior
  }, []);

  /**
   * Guarda una búsqueda reciente en localStorage.
   * @param {Object} item - Item a guardar { titulo, menu, path, icono, tipo, semCode, tituloModulo }
   */
  const guardarReciente = useCallback(
    (item) => {
      if (!usuarioId || !item?.path) {
        return;
      }
      setRecientes({ append: item });
    },
    [setRecientes]
  );

  /**
   * Elimina una búsqueda reciente por su path.
   * @param {string} path - Path del item a eliminar
   */
  const eliminarReciente = useCallback(
    (path) => {
      if (!usuarioId) {
        return;
      }
      setRecientes({ remove: true, predicate: (item) => item.path === path });
    },
    [setRecientes]
  );

  /**
   * Limpia todas las búsquedas recientes.
   */
  const limpiarRecientes = useCallback(() => {
    if (!usuarioId) {
      return;
    }
    setRecientes({ clear: true });
  }, [setRecientes]);

  return {
    recientes,
    setRecientes,
    cargarRecientes,
    guardarReciente,
    eliminarReciente,
    limpiarRecientes,
    tieneRecientes: recientes.length > 0,
  };
};

export default useRecientes;
